import { FC } from "react";

import { formatCurrencyAmount, getDollarValue } from "@simplyk/common";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";

interface RenewalPriceChangeInfoboxProps {
  memberCount: number;
  newAmount: number;
  previousAmount: number;
  isGroup?: boolean;
}

export const RenewalPriceChangeInfobox: FC<RenewalPriceChangeInfoboxProps> = ({
  memberCount,
  newAmount,
  previousAmount,
  isGroup = false,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organizationCurrency } = useCurrentUserContext();

  const formatAmount = (amount: number) =>
    formatCurrencyAmount({ value: getDollarValue(amount), locale, currency: organizationCurrency });

  return (
    <DashboardInfobox size="small" vibe="brand" data-test="renewal-price-change-infobox">
      {t(
        "dashboard",
        RENEWAL_PRICE_CHANGE_KEYS[isGroup ? "group" : "member"][newAmount > previousAmount ? "increase" : "decrease"],
        { count: memberCount, newAmount: formatAmount(newAmount), previousAmount: formatAmount(previousAmount) }
      )}
    </DashboardInfobox>
  );
};

const RENEWAL_PRICE_CHANGE_KEYS = {
  member: {
    increase: "ticketing.renewalPriceChange.increase",
    decrease: "ticketing.renewalPriceChange.decrease",
  },
  group: {
    increase: "ticketing.renewalPriceChange.groupIncrease",
    decrease: "ticketing.renewalPriceChange.groupDecrease",
  },
} as const;
